import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiArrowDown, FiArrowUp, FiGrid, FiLock, FiTrash2, FiUser } from 'react-icons/fi';
import { changePassword, getCashRegisters, getMe, getProducts } from '../api';
import useCurrentUser from '../hooks/useCurrentUser';
import useMyPreferences from '../hooks/useMyPreferences';
import { CLIENT_TYPE_OPTIONS, normalizeClientType, resolvePriceClientType } from '../utils/clientType';
import { normalizePaginatedResponse } from '../utils/pagination';
import { hasPermission } from '../utils/permissions';
import {
  buildResolvedPosSlots,
  getPosSlotLayout,
  movePosSlot,
  removePosSlotAt,
  setPosSlotLayout,
} from '../utils/posSlotLayouts';

const MyProfilePage = () => {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cashRegisters, setCashRegisters] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCashRegisterId, setSelectedCashRegisterId] = useState('');
  const [slotLoading, setSlotLoading] = useState(false);
  const [slotSaving, setSlotSaving] = useState(false);
  const [slotMessage, setSlotMessage] = useState('');
  const [slotError, setSlotError] = useState('');
  const { user } = useCurrentUser();
  const {
    preferences,
    loading: preferencesLoading,
    error: preferencesError,
    savePreferences,
  } = useMyPreferences();
  const canUpdateProfile = hasPermission(user, 'profile.update');
  const selectedBranchId = useMemo(() => localStorage.getItem('selectedBranchId') || '', []);
  const displayName = name || 'Usuario';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await getMe();
        setName(profile.name);
        setUsername(profile.username);
      } catch (requestError) {
        console.log(requestError);
        setError('Error al cargar el perfil.');
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    if (!selectedBranchId) {
      setCashRegisters([]);
      setProducts([]);
      setSelectedCashRegisterId('');
      return;
    }

    let ignore = false;
    const fetchPosSlotContext = async () => {
      setSlotLoading(true);
      setSlotError('');
      try {
        const [cashRegisterResponse, productResponse] = await Promise.all([
          getCashRegisters(selectedBranchId, { pageSize: 0 }),
          getProducts(selectedBranchId, { pageSize: 0 }),
        ]);
        if (ignore) return;
        const nextCashRegisters = normalizePaginatedResponse(cashRegisterResponse).items;
        const nextProducts = normalizePaginatedResponse(productResponse).items;
        setCashRegisters(nextCashRegisters);
        setProducts(nextProducts);
        setSelectedCashRegisterId((current) => {
          if (current && nextCashRegisters.some((entry) => String(entry.id) === String(current))) {
            return current;
          }
          return nextCashRegisters[0]?.id ? String(nextCashRegisters[0].id) : '';
        });
      } catch (requestError) {
        if (ignore) return;
        console.log(requestError);
        setSlotError('Error al cargar cajas o productos para configurar los favs.');
        setCashRegisters([]);
        setProducts([]);
        setSelectedCashRegisterId('');
      } finally {
        if (!ignore) setSlotLoading(false);
      }
    };

    fetchPosSlotContext();
    return () => {
      ignore = true;
    };
  }, [selectedBranchId]);

  useEffect(() => {
    setSlotMessage('');
    setSlotError('');
  }, [selectedCashRegisterId]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!canUpdateProfile) return;

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    if (newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }

    try {
      setLoading(true);
      await changePassword({ password: currentPassword, newPassword });
      setMessage('La contraseña ha sido actualizada exitosamente.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Error al cambiar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  const activeCashRegister = useMemo(
    () => cashRegisters.find((entry) => String(entry.id) === String(selectedCashRegisterId)) || null,
    [cashRegisters, selectedCashRegisterId]
  );

  const registerAllowedClientTypes = useMemo(() => {
    const normalized = [];
    const seen = new Set();
    (activeCashRegister?.allowedClientTypes || []).forEach((entry) => {
      const value = normalizeClientType(entry);
      if (!value || seen.has(value)) return;
      normalized.push(value);
      seen.add(value);
    });
    return normalized;
  }, [activeCashRegister?.allowedClientTypes]);

  const registerAllowedClientTypeLabels = useMemo(() => {
    if (!registerAllowedClientTypes.length) return 'Todos los tipos';
    return CLIENT_TYPE_OPTIONS
      .filter((option) => registerAllowedClientTypes.includes(option.value))
      .map((option) => option.label)
      .join(', ');
  }, [registerAllowedClientTypes]);

  const isPriceAllowedByRegister = useCallback((price) => {
    if (!price || price.price === null || price.price === undefined) return false;
    if (!registerAllowedClientTypes.length) return true;
    const priceType = normalizeClientType(resolvePriceClientType(price));
    return registerAllowedClientTypes.includes(priceType);
  }, [registerAllowedClientTypes]);

  const currentPosSlotLayout = useMemo(
    () => getPosSlotLayout(preferences?.posSlotLayouts, selectedBranchId, selectedCashRegisterId, products),
    [preferences?.posSlotLayouts, products, selectedBranchId, selectedCashRegisterId]
  );

  const resolvedPosSlots = useMemo(() => buildResolvedPosSlots({
    products,
    slotLayout: currentPosSlotLayout,
    quantities: {},
    discounts: {},
    isPriceAllowedByRegister,
    resolveDisplayPrice: (_variety, price) => ({
      price: Number(price?.price || 0),
      taxFreeApplied: false,
    }),
  }), [currentPosSlotLayout, isPriceAllowedByRegister, products]);

  const persistCurrentSlotLayout = useCallback(async (nextLayout, successText) => {
    if (!selectedBranchId || !selectedCashRegisterId) return;
    setSlotSaving(true);
    setSlotMessage('');
    setSlotError('');
    try {
      const nextLayouts = setPosSlotLayout(
        preferences?.posSlotLayouts,
        selectedBranchId,
        selectedCashRegisterId,
        nextLayout,
        products
      );
      await savePreferences({ posSlotLayouts: nextLayouts });
      setSlotMessage(successText);
    } catch (requestError) {
      console.log(requestError);
      setSlotError(requestError?.message || 'No se pudieron guardar los favs.');
    } finally {
      setSlotSaving(false);
    }
  }, [preferences?.posSlotLayouts, products, savePreferences, selectedBranchId, selectedCashRegisterId]);

  const handleMoveSlot = useCallback((fromIndex, toIndex) => {
    const nextLayout = movePosSlot(currentPosSlotLayout, fromIndex, toIndex);
    persistCurrentSlotLayout(nextLayout, 'Orden de favs actualizado.');
  }, [currentPosSlotLayout, persistCurrentSlotLayout]);

  const handleRemoveSlot = useCallback((slotIndex) => {
    const nextLayout = removePosSlotAt(currentPosSlotLayout, slotIndex);
    persistCurrentSlotLayout(nextLayout, 'Fav eliminado de la configuración.');
  }, [currentPosSlotLayout, persistCurrentSlotLayout]);

  const slotStatus = slotError || preferencesError;
  const slotsDisabled = slotLoading || preferencesLoading || slotSaving || !selectedCashRegisterId;

  return (
    <div className="min-h-screen bg-gray-100 pt-24 px-4 pb-10">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl shadow p-6 border border-blue-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-lg font-semibold">
                {initials}
              </div>
              <div>
                <h1 className="text-2xl font-bold">Mi Perfil</h1>
                <p className="text-sm text-gray-600">
                  Administra tus datos, tu seguridad y tus favs personales del POS.
                </p>
              </div>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full ${
              canUpdateProfile ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {canUpdateProfile ? 'Puedes actualizar tu contrasena' : 'Sin permisos de edicion'}
            </span>
          </div>
        </div>

        {(message || error) && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${
            message
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {message || error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6">
          <div className="bg-white rounded-2xl shadow p-6 border border-gray-100">
            <div className="flex items-center gap-2 mb-4 text-gray-700">
              <FiUser />
              <h2 className="text-lg font-semibold">Datos personales</h2>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Nombre</label>
                <input
                  type="text"
                  value={name}
                  disabled
                  className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Nombre de Usuario</label>
                <input
                  type="text"
                  value={username}
                  disabled
                  className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50 text-gray-700"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow p-6 border border-gray-100">
            <div className="flex items-center gap-2 mb-4 text-gray-700">
              <FiLock />
              <h2 className="text-lg font-semibold">Seguridad</h2>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              La nueva contrasena debe tener al menos 8 caracteres.
            </p>
            <form onSubmit={handlePasswordChange}>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">Contraseña Actual</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3"
                  required
                  disabled={!canUpdateProfile}
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">Nueva Contraseña</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3"
                  required
                  disabled={!canUpdateProfile}
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">Confirmar Nueva Contraseña</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3"
                  required
                  disabled={!canUpdateProfile}
                />
              </div>
              <div className="flex justify-end">
                {canUpdateProfile && (
                  <button
                    type="submit"
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-300"
                    disabled={loading}
                  >
                    {loading ? 'Actualizando...' : 'Cambiar Contraseña'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow p-6 border border-gray-100">
          <div className="flex items-center gap-2 mb-4 text-gray-700">
            <FiGrid />
            <h2 className="text-lg font-semibold">Favs POS</h2>
          </div>
          <p className="text-sm text-gray-600">
            Ordena o elimina los favs guardados para tu usuario. Los nuevos favs se agregan desde la vista
            <span className="font-semibold"> Favs </span>
            del POS.
          </p>

          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Sucursal actual</label>
                <input
                  type="text"
                  value={selectedBranchId || 'No seleccionada'}
                  disabled
                  className="w-full border border-gray-200 rounded-lg p-3 bg-gray-50 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Caja</label>
                <select
                  value={selectedCashRegisterId}
                  onChange={(e) => setSelectedCashRegisterId(e.target.value)}
                  disabled={!selectedBranchId || slotLoading || !cashRegisters.length}
                  className="w-full border border-gray-300 rounded-lg p-3 bg-white text-gray-700 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  {!cashRegisters.length && <option value="">Sin cajas disponibles</option>}
                  {cashRegisters.map((register) => (
                    <option key={register.id} value={register.id}>
                      {register.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                <div className="font-semibold text-gray-800">Tipos permitidos</div>
                <div className="mt-1">{registerAllowedClientTypeLabels}</div>
              </div>
            </div>

            <div className="space-y-3">
              {slotMessage && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {slotMessage}
                </div>
              )}
              {slotStatus && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {slotStatus}
                </div>
              )}
              {!selectedBranchId && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  Selecciona una sucursal en la aplicación para administrar los favs de esa caja.
                </div>
              )}
              {selectedBranchId && slotLoading && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                  Cargando cajas y productos...
                </div>
              )}
              {selectedBranchId && !slotLoading && !cashRegisters.length && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                  No hay cajas disponibles en la sucursal actual.
                </div>
              )}
              {selectedBranchId && !slotLoading && selectedCashRegisterId && resolvedPosSlots.length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center text-sm text-gray-500">
                  No tienes favs guardados para esta caja.
                </div>
              )}
              {selectedBranchId && !slotLoading && resolvedPosSlots.map((slot, index) => (
                <div
                  key={slot.key}
                  className={`rounded-xl border px-4 py-4 ${
                    slot.disabled
                      ? 'border-amber-200 bg-amber-50/60'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        Slot {index + 1}
                      </div>
                      <div className="mt-1 text-base font-semibold text-gray-900">
                        {slot.productName}
                      </div>
                      {slot.detailLabel ? (
                        <div className="mt-1 text-sm text-gray-600">{slot.detailLabel}</div>
                      ) : null}
                      <div className="mt-2 text-sm text-gray-500">
                        {slot.priceLabel}
                      </div>
                      {slot.invalidReason ? (
                        <div className="mt-2 text-xs text-amber-700">
                          {slot.invalidReason}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleMoveSlot(index, index - 1)}
                        disabled={slotsDisabled || index === 0}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <FiArrowUp />
                        Subir
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveSlot(index, index + 1)}
                        disabled={slotsDisabled || index === resolvedPosSlots.length - 1}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <FiArrowDown />
                        Bajar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveSlot(index)}
                        disabled={slotsDisabled}
                        className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <FiTrash2 />
                        Quitar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyProfilePage;
